import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Popup } from 'react-map-gl/maplibre';
import { Separator, cn } from 'shared-ui';

import type { EntityClusterBreakdownRow, EntityClusterSummary } from '../utils/entityClusterProperties';
import { leakMarkerImageId, poiMarkerImageId } from '../utils/entityMarkerArt';
import { getLeakEntityStatusDefinition, getPoiEntityStatusDefinition } from '../utils/entityStatus';
import type { LeakEntityStatus, PoiEntityStatus } from '../utils/entityStatus';
import { StatusMarkerIcon } from './StatusMarkerIcon';

// Cluster circles are centre-anchored with a max radius of 24px + 2px stroke, so a
// scalar offset (unlike the per-anchor marker offset used by PoiPopup) clears the
// bubble on whichever side MapLibre picks.
const CLUSTER_POPUP_OFFSET = 28;

/** The Network Explorer's subtitle: a bubble there may hold activity from several networks. */
export const EXPLORER_CLUSTER_SUBTITLE = 'In this network(s)';

// ─── Designer display orders (presentation only — registry order is unchanged) ────────
//
// The card lists every ENABLED status in the designer's order, zeros included, so a
// cluster always shows the complete vocabulary. Disabled statuses (unsuitable and the
// recheck lifecycle) are filtered out by the registry's enabled flag — they render
// nowhere until activated, at which point these lists pick them up with no further work.

const POI_DISPLAY_ORDER: readonly PoiEntityStatus[] = [
    'awaiting_assessment',
    'leak_found',
    'no_leak_found',
    'unable_to_check',
    'inconclusive',
    'unclassified',
];

// The leaks section is two groups split by a dashed divider: primary outcomes, then the
// follow-up / needs-attention statuses.
const LEAK_PRIMARY_ORDER: readonly LeakEntityStatus[] = [
    'leak_found',
    'already_marked_up',
    'no_dig_repaired',
    'repaired',
    'unsuitable',
    'no_leak_found',
];
const LEAK_FOLLOW_UP_ORDER: readonly LeakEntityStatus[] = [
    'rechecked',
    'unable_to_check',
    'inconclusive',
    'initial_repair_failed',
    'recheck_failed',
    'unclassified',
];

interface DisplayRow {
    key: string;
    label: string;
    imageId: string;
    count: number;
}

// Counts come off the accumulated rows by counter key; an ordered status absent from the
// rows simply reads 0 (the reader omits zero rows, but the vocabulary is known here).
function buildDisplayRows(
    family: 'poi' | 'leak',
    order: readonly (PoiEntityStatus | LeakEntityStatus)[],
    rows: EntityClusterBreakdownRow[],
): DisplayRow[] {
    const countByKey = new Map(rows.map((row) => [row.key, row.count]));
    return order
        .filter((status) =>
            family === 'poi'
                ? getPoiEntityStatusDefinition(status as PoiEntityStatus).enabled
                : getLeakEntityStatusDefinition(status as LeakEntityStatus).enabled,
        )
        .map((status) => {
            const key = `count_${family}_${status}`;
            return {
                key,
                label:
                    family === 'poi'
                        ? getPoiEntityStatusDefinition(status as PoiEntityStatus).label
                        : getLeakEntityStatusDefinition(status as LeakEntityStatus).label,
                imageId:
                    family === 'poi'
                        ? poiMarkerImageId(status as PoiEntityStatus)
                        : leakMarkerImageId(status as LeakEntityStatus),
                count: countByKey.get(key) ?? 0,
            };
        });
}

/** The reader's residual row (a status shipped backend-first) — appended last when present. */
function otherRow(family: 'poi' | 'leak', rows: EntityClusterBreakdownRow[]): DisplayRow | null {
    const other = rows.find((row) => row.key === `other_${family}`);
    if (!other) return null;
    return {
        key: other.key,
        label: other.label,
        imageId: family === 'poi' ? poiMarkerImageId('unclassified') : leakMarkerImageId('unclassified'),
        count: other.count,
    };
}

function StatusRowList({ rows }: { rows: DisplayRow[] }) {
    return (
        <ul>
            {rows.map((row) => (
                <li key={row.key} className="flex items-center gap-2 py-0.5">
                    <StatusMarkerIcon imageId={row.imageId} className="h-6 w-5 shrink-0 object-contain" />
                    <span className="text-muted-foreground min-w-0 truncate text-sm leading-5 font-normal tracking-normal">
                        {row.label}
                    </span>
                    <span className="text-origin-blue ml-auto text-sm leading-5 font-normal tracking-normal tabular-nums">
                        {row.count.toLocaleString()}
                    </span>
                </li>
            ))}
        </ul>
    );
}

function EntitySection({
    heading,
    count,
    expanded,
    onToggle,
    children,
}: {
    heading: string;
    count: number;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    const Chevron = expanded ? ChevronUp : ChevronDown;
    return (
        <div>
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="flex w-full cursor-pointer items-center gap-2 py-1"
            >
                <span
                    className={cn(
                        'text-sm leading-5 font-black tracking-normal uppercase',
                        expanded ? 'text-origin-blue' : 'text-foreground',
                    )}
                >
                    {heading}
                </span>
                <span
                    className={cn(
                        'text-sm leading-5 font-normal tracking-normal tabular-nums',
                        expanded ? 'text-origin-blue' : 'text-muted-foreground',
                    )}
                >
                    {count.toLocaleString()}
                </span>
                <Chevron
                    className={cn('ml-auto size-4 shrink-0', expanded ? 'text-origin-blue' : 'text-foreground')}
                    aria-hidden
                />
            </button>
            {expanded && children}
        </div>
    );
}

/**
 * The cluster breakdown card body, shared by the single-client hover card and the Network
 * Explorer's click-pinned card: the two differ only in their anchor behaviour and subtitle.
 */
export function EntityClusterCard({
    entitySummary,
    subtitle = 'In this cluster',
}: {
    entitySummary: EntityClusterSummary;
    /** The line under "POI's & Leaks" — what the total is the total of. */
    subtitle?: string;
}) {
    const [expanded, setExpanded] = useState<'pois' | 'leaks' | null>(null);

    const poiRows = [
        buildDisplayRows('poi', POI_DISPLAY_ORDER, entitySummary.poiRows),
        otherRow('poi', entitySummary.poiRows),
    ]
        .flat()
        .filter((row): row is DisplayRow => row !== null);
    const leakPrimaryRows = buildDisplayRows('leak', LEAK_PRIMARY_ORDER, entitySummary.leakRows);
    const leakFollowUpRows = [
        buildDisplayRows('leak', LEAK_FOLLOW_UP_ORDER, entitySummary.leakRows),
        otherRow('leak', entitySummary.leakRows),
    ]
        .flat()
        .filter((row): row is DisplayRow => row !== null);

    return (
        <>
            <div className="flex items-start gap-2.5">
                <span className="text-origin-blue text-2xl leading-8 font-extrabold tracking-normal tabular-nums">
                    {entitySummary.total.toLocaleString()}
                </span>
                <span className="min-w-0">
                    <span className="text-foreground block text-sm leading-5 font-black tracking-normal uppercase">
                        POI&#39;s &amp; Leaks
                    </span>
                    <span className="text-muted-foreground block text-xs leading-4 font-normal tracking-normal">
                        {subtitle}
                    </span>
                </span>
            </div>

            <Separator className="my-2" />

            <EntitySection
                heading="POI's"
                count={entitySummary.poiCount}
                expanded={expanded === 'pois'}
                onToggle={() => {
                    setExpanded((current) => (current === 'pois' ? null : 'pois'));
                }}
            >
                <StatusRowList rows={poiRows} />
            </EntitySection>

            <EntitySection
                heading="Leaks"
                count={entitySummary.leakCount}
                expanded={expanded === 'leaks'}
                onToggle={() => {
                    setExpanded((current) => (current === 'leaks' ? null : 'leaks'));
                }}
            >
                <>
                    <StatusRowList rows={leakPrimaryRows} />
                    <div className="border-border my-1 border-t border-dashed" />
                    <StatusRowList rows={leakFollowUpRows} />
                </>
            </EntitySection>
        </>
    );
}

interface PoiClusterPopupProps {
    /**
     * The split-model breakdown (POIs / Leaks, each summing to its family count — a
     * leaks-only cluster legitimately shows 0 POIs).
     */
    entitySummary: EntityClusterSummary;
    /** Geographic anchor — MapLibre projects + tracks this itself, so no screen-pixel math here. */
    longitude: number;
    latitude: number;
    /** Wired to the cluster interactions so hovering onto the card cancels its dismiss timer. */
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    /** The line under the total; defaults to the single-client "In this cluster". */
    subtitle?: string;
}

/**
 * Hover card for POI cluster bubbles: a prominent combined total, then one collapsible
 * section per family (accordion — at most one open), each listing the complete enabled
 * status vocabulary in the designer's order with zeros included and counts right-aligned.
 * The section totals always sum to the bubble's count (see readEntityClusterSummary).
 * Positioned by MapLibre's own `<Popup>`, matching the PoiPopup chrome; the interactions
 * hook keeps the card alive while the pointer is over it, so the section toggles are
 * reliably clickable.
 */
export function PoiClusterPopup({
    entitySummary,
    longitude,
    latitude,
    onMouseEnter,
    onMouseLeave,
    subtitle,
}: PoiClusterPopupProps) {
    return (
        <Popup
            longitude={longitude}
            latitude={latitude}
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={CLUSTER_POPUP_OFFSET}
            maxWidth="none"
            className="orbit-hover-popup"
        >
            <div
                className="border-border/30 bg-card w-80 rounded-md border p-3 shadow-lg"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {/* Keyed by the anchor so moving to a different cluster remounts the card
                    with both sections collapsed; hovering the same cluster keeps whatever
                    the user expanded. */}
                <EntityClusterCard
                    key={`${String(longitude)}:${String(latitude)}`}
                    entitySummary={entitySummary}
                    subtitle={subtitle}
                />
            </div>
        </Popup>
    );
}
